import { useCallback, useEffect, useMemo, useState } from "react";
import { compact } from "lodash";
import {
  ArtQuizProductFragment,
  ProductCollectionSortKeys,
  useGetComplimentaryProductsLazyQuery,
  useGetProductsForArtQuizLazyQuery,
} from "../../generated/storefront";
import { quizToTagMapping } from "./quizMapping";
import { getIdNumber } from "../../helpers/shopify";
import { useAppState } from "../../state";

export type PrintGroup = {
  id: string;
  link: string;
  products: ArtQuizProductFragment[];
};

const useArtQuiz = () => {
  const { selectedCountry } = useAppState();
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);
  const [selectedStyles, setSelectedStyles] = useState<string[]>([]);
  const [selectedMoods, setSelectedMoods] = useState<string[]>([]);
  const [doubles, setDoubles] = useState<PrintGroup[]>([]);
  const [galleryWalls, setGalleryWalls] = useState<PrintGroup[]>([]);
  const [getComplimentaryProducts] = useGetComplimentaryProductsLazyQuery();

  const getSelectedTags = useCallback(() => {
    const tags: string[] = [];

    // Add subject tags
    selectedSubjects.forEach((subject) => {
      const tagsForSubject = quizToTagMapping.subject[subject as keyof typeof quizToTagMapping.subject] || [];
      tags.push(...tagsForSubject);
    });

    // Add style tags
    selectedStyles.forEach((style) => {
      const tagsForStyle = quizToTagMapping.style[style as keyof typeof quizToTagMapping.style] || [];
      tags.push(...tagsForStyle);
    });

    // Add mood tags
    selectedMoods.forEach((mood) => {
      const tagsForMood = quizToTagMapping.mood[mood as keyof typeof quizToTagMapping.mood] || [];
      tags.push(...tagsForMood);
    });

    return Array.from(new Set(tags)); // Convert Set to array
  }, [selectedSubjects, selectedStyles, selectedMoods]);

  const productFilters = useMemo(() => {
    const tags = getSelectedTags();
    return tags.map((tag) => ({ tag: `gm.${tag}` }));
  }, [getSelectedTags]);

  const [getProducts, { data, loading, fetchMore }] = useGetProductsForArtQuizLazyQuery({
    variables: {
      productFilters,
      sortKey: ProductCollectionSortKeys.BestSelling,
      limit: 12,
      country: selectedCountry,
    },
  });

  const hasMore = useMemo(() => data?.collection?.products.pageInfo.hasNextPage || false, [data]);
  const loadMore = useCallback(async () => {
    await fetchMore({
      variables: {
        afterCursor: data?.collection?.products.pageInfo.endCursor,
      },
    });
  }, [fetchMore, data]);

  const applyComplimentaryProducts = useCallback(
    async (prods: ArtQuizProductFragment[], groupSize: number) => {
      const promises = prods.map(async (product) => {
        const { data } = await getComplimentaryProducts({
          variables: {
            productId: product.id,
            country: selectedCountry,
          },
        });
        const complimentaryProducts = (data?.productRecommendations || []).filter((p) => p.tags.includes("favourite"));
        const selectedTags = getSelectedTags();
        const filteredComplimentaryProducts = complimentaryProducts.filter((p) =>
          selectedTags.some((tag) => p.tags.includes(`gm.${tag}`))
        );
        const orderedComplimentaryProducts = [
          ...filteredComplimentaryProducts,
          ...complimentaryProducts.filter((p) => !filteredComplimentaryProducts.includes(p)),
        ];
        if (orderedComplimentaryProducts.length === 0 || orderedComplimentaryProducts.length < (groupSize || 2)) return null;
        const productsToDisplay = groupSize ? orderedComplimentaryProducts.slice(0, groupSize - 1) : orderedComplimentaryProducts;
        const productIds = [getIdNumber(product.id), ...productsToDisplay.map((p) => getIdNumber(p.id))];
        return {
          id: product.id,
          link: groupSize > 2 ? `/gallery-wall?products=${productIds.join(",")}` : `/print-set?products=${productIds.join(",")}`,
          products: [product, ...productsToDisplay],
        };
      });
      return compact(await Promise.all(promises));
    },
    [getComplimentaryProducts, getSelectedTags, selectedCountry]
  );

  const products = useMemo(
    () => data?.collection?.products.nodes.filter((node) => node.__typename === "Product") as ArtQuizProductFragment[],
    [data]
  );
  const singles = useMemo(() => {
    return (
      products?.map((product) => ({
        id: product.id,
        link: `/products/${product.handle}`,
        products: [product],
      })) || []
    );
  }, [products]);

  const getPrintCombinations = async (products: ArtQuizProductFragment[]) => {
    const newProducts = products.filter((p) => doubles.every((d) => d.products[0].id !== p.id));
    const newDoubles = await applyComplimentaryProducts(newProducts, 2);
    const newGalleryWalls = await applyComplimentaryProducts(newProducts, 5);
    setDoubles((prev) => [...prev, ...newDoubles]);
    setGalleryWalls((prev) => [...prev, ...newGalleryWalls]);
  };

  useEffect(() => {
    if (products) {
      getPrintCombinations(products);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  return {
    singles,
    doubles,
    galleryWalls,
    getProducts,
    selectedSubjects,
    setSelectedSubjects,
    selectedStyles,
    setSelectedStyles,
    selectedMoods,
    setSelectedMoods,
    loading,
    hasMore,
    loadMore,
  };
};

export default useArtQuiz;
